/**
 * Copie exploitable par le code des jetons de la section 03 du cahier des charges.
 *
 * `theme.css` reste la déclaration qui fait foi pour le rendu ; ce fichier est la copie
 * lisible par les tests. Un test vérifie que les deux ne divergent pas
 * (`contrast.test.ts`), de sorte qu'une valeur modifiée d'un seul côté fait échouer la suite.
 */

/** Fondations neutres — section 03.1. */
export const neutrals = {
  surface: '#FAF8F4',
  card: '#FFFFFF',
  sunken: '#EDEAE3',
  border: '#DAD5CB',
  ink: '#1A1D1B',
  inkMuted: '#6B6F6A',
  accent: '#3F6B5B',
  accentHover: '#2E5044',
  accentSoft: '#E7EFE9',
  /** Le seul rouge du système, réservé à la suppression définitive. */
  danger: '#A33A2B',
  skeletonImage: '#F4F1EB',
  /**
   * Couleur des lettres sur une marque de catégorie.
   *
   * Rendue explicite plutôt que reprise de `card` : en mode sombre, `card` devient foncé et les
   * lettres deviendraient illisibles sur leur carré coloré. Les marques ne changent pas d'un
   * mode à l'autre — le blanc y reste lisible dans les deux — donc cette valeur non plus.
   */
  onMark: '#FFFFFF',
} as const

/**
 * Palette de catégories. La section 03.2 en fixait six ; la limite est passée à dix
 * (décision L13), les quatre dernières étant générées aux mêmes clarté et saturation.
 * `fill` est l'aplat (bandeaux, en-têtes) ; `mark` est le fond du carré à deux lettres,
 * dont les lettres sont blanches.
 */
export const categoryPalette = [
  { hue: 0, initials: 'Re', seedName: 'Recettes', fill: '#F2E2C9', mark: '#996B2C' },
  { hue: 1, initials: 'Ra', seedName: 'Randos', fill: '#D6E8DA', mark: '#3F6B5B' },
  { hue: 2, initials: 'Ca', seedName: 'Cadeaux', fill: '#F4DCE0', mark: '#A0546A' },
  { hue: 3, initials: 'Vo', seedName: 'Voyages', fill: '#D9E1F2', mark: '#4A5F94' },
  { hue: 4, initials: 'So', seedName: 'Sorties', fill: '#E7DCEF', mark: '#6E5490' },
  { hue: 5, initials: 'Co', seedName: 'Courses', fill: '#E4E9CF', mark: '#6C7A3A' },
  // Quatre teintes ajoutées le 2026-08-19 pour passer la limite de six à dix (décision L13).
  // Générées aux mêmes clarté et saturation que les six premières, placées dans les plus grands
  // écarts du cercle chromatique. Elles n'ont pas de nom d'amorce : seules les six premières
  // peuplent un espace neuf.
  { hue: 6, initials: 'Br', seedName: null, fill: '#EFD9D5', mark: '#8B4D44' },
  { hue: 7, initials: 'Ja', seedName: null, fill: '#D6EFD5', mark: '#43833F' },
  { hue: 8, initials: 'La', seedName: null, fill: '#D5E9EF', mark: '#447C8B' },
  { hue: 9, initials: 'Pr', seedName: null, fill: '#EFD5ED', mark: '#8B4488' },
] as const

/**
 * Palette sombre — décision L27.
 *
 * Elle n'inverse pas la palette claire : un fond noir pur et un blanc pur fatiguent, et les
 * pastels de la palette claire deviendraient éblouissants. Les valeurs sont choisies dans le
 * même registre chaud verdâtre, et chaque contraste a été calculé avant d'être retenu — les
 * tests de `contrast.test.ts` les vérifient au même titre que la palette claire.
 */
export const neutralsDark = {
  surface: '#151917',
  card: '#1E2320',
  sunken: '#0F1211',
  border: '#343C37',
  ink: '#F1EFE9',
  inkMuted: '#A8AFA8',
  /**
   * Vert clairci : le vert foncé de la palette claire ne se détacherait pas d'un fond sombre.
   * Les libellés de bouton y passent donc en encre foncée, ce que `card` fournit déjà.
   */
  accent: '#8FC4AC',
  accentHover: '#A8D4BF',
  accentSoft: '#22332B',
  danger: '#F0897C',
  skeletonImage: '#232823',
  onMark: '#FFFFFF',
} as const

/**
 * Aplats de catégorie en mode sombre : version foncée et désaturée de chaque teinte.
 *
 * Les marques, elles, sont identiques dans les deux modes : le blanc y est lisible quel que soit
 * le fond, et leur donner une variante aurait fait deux valeurs à tenir d'accord pour rien.
 */
export const categoryFillsDark = [
  '#3A2E1C',
  '#1F332A',
  '#3A222A',
  '#212940',
  '#2C2338',
  '#282D18',
  '#3A2320',
  '#1D3520',
  '#1C3038',
  '#38203A',
] as const

/** Nombre maximal de catégories vivantes par espace — décision L13, était six. */
export const maximumCategories = 10

export type CategoryHue = (typeof categoryPalette)[number]['hue']

/**
 * Tailles autorisées pour la marque de catégorie — correction E9.
 * Le cahier rend les marques de 9 à 17 px selon les planches, alors que la section 03.3 fixe un
 * plancher absolu de 13 px. Trois tailles seulement sont permises, aucune sous ce plancher.
 */
export const markSizes = {
  /** Grille de catégories, en-tête de fiche. */
  large: { square: 44, font: 17 },
  /** Bandeau de carte en mosaïque. */
  medium: { square: 40, font: 15 },
  /** Rangées compactes (variante 1h) — la plus petite permise. */
  small: { square: 32, font: 13 },
} as const

export type MarkSize = keyof typeof markSizes

/** Échelle d'espacement fermée — section 03.4. « Aucune autre valeur. » */
export const spacingScale = [4, 8, 12, 16, 24, 32, 48] as const

/**
 * Dimensions du gabarit d'écran — section 03.4.
 * Distinctes de l'échelle d'espacement : le cahier ferme l'échelle à sept valeurs tout en
 * fixant une marge latérale de 20 px et des hauteurs de 64 et 76 px, qui n'y figurent pas.
 * Elles sont donc traitées comme des jetons de gabarit. Voir l'écart E8 de docs/00-references.md.
 */
export const layout = {
  gutter: 20,
  header: 64,
  tabBar: 64,
  /** Décollement de la barre basse par rapport au bord (décision L49). */
  tabBarInset: 12,
  gridGap: 12,
  /** Largeur de la colonne mobile maquettée, conservée centrée sur grand écran (décision A3). */
  column: 390,
  primaryButtonHeight: 56,
  minimumTouchTarget: 48,
} as const

/**
 * La place que prend la barre basse, mesurée depuis le bas de la fenêtre.
 *
 * Calculée ici pour tout le monde. La barre s'est mise à flotter (décision L49) : elle n'occupe
 * plus sa seule hauteur, mais aussi son décollement et la zone sûre du téléphone. Les écrans qui
 * la contournaient en comptant « hauteur de barre » sont passés dessous — le champ d'ajout des
 * courses s'y est caché, et le défaut n'était visible que sur un vrai téléphone (correction S69).
 *
 * @param supplement écart voulu au-dessus de la barre, en pixels.
 */
export function sousLaBarre(supplement = 0) {
  return `calc(${layout.tabBar + layout.tabBarInset + supplement}px + env(safe-area-inset-bottom))`
}

/** Mouvement — section 03.6. Aucun rebond, aucune animation décorative. */
export const motion = {
  sheetMs: 260,
  navigationMs: 200,
  feedbackMs: 120,
  skeletonPulseMs: 1400,
  /** Durée du bandeau d'annulation imposée par la section 06. */
  undoMs: 4000,
} as const
